import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const MAX_ATTEMPTS = 12;
const INTERVAL_MS = 2000;

/**
 * Polls /api/payments/status/{sessionId} until paid, expired, or maxed out.
 * Returns { status: 'polling' | 'paid' | 'expired' | 'timeout' | 'error', info }.
 */
export function usePaymentPolling(sessionId) {
  const [state, setState] = useState({ status: "polling", info: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", info: { message: "Missing session id" } });
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;

    const finish = (next) => {
      if (!cancelled) setState(next);
    };

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (r.data.payment_status === "paid") {
          finish({ status: "paid", info: r.data });
          return;
        }
        if (r.data.status === "expired") {
          finish({ status: "expired", info: r.data });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          finish({ status: "timeout", info: r.data });
          return;
        }
        finish({ status: "polling", info: r.data });
        setTimeout(poll, INTERVAL_MS);
      } catch (e) {
        finish({
          status: "error",
          info: { message: e.response?.data?.detail || "Status check failed" },
        });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return state;
}
